/**
 * OptionsPage — Bloomberg-style options analysis.
 * Chain (calls left / puts right), max pain, IV rank gauge, unusual activity.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  Layers, Search, TrendingUp, TrendingDown, AlertTriangle,
  BarChart2, ArrowUp, ArrowDown,
} from 'lucide-react'
import { useOptions } from '@/hooks/useOptions'
import { clsx } from 'clsx'
import type { OptionContract, UnusualOptionTrade } from '@/lib/types'
import PageHeader from '@/components/ui/PageHeader'

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number | null | undefined, decimals = 2, prefix = ''): string {
  if (n == null) return 'N/A'
  return `${prefix}${n.toFixed(decimals)}`
}

function fmtK(n: number | null | undefined): string {
  if (n == null) return 'N/A'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function fmtPremium(n: number | null | undefined): string {
  if (n == null) return 'N/A'
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`
  return `$${n.toFixed(0)}`
}

// ── IV Rank Gauge ─────────────────────────────────────────────────────────────

function IVRankGauge() {
  const { ivRank, ivRankLoading, ivRankError } = useOptions()

  if (ivRankLoading) return (
    <div className="card p-4">
      <p className="text-text-muted text-sm animate-pulse">Loading IV Rank...</p>
    </div>
  )
  if (ivRankError || !ivRank) return (
    <div className="card p-4">
      <p className="text-text-muted text-xs">{ivRankError ?? 'IV rank unavailable'}</p>
    </div>
  )

  const rank = ivRank.iv_rank ?? 0
  const color = rank >= 80 ? '#f43f5e' : rank >= 60 ? '#f59e0b' : rank <= 20 ? '#10b981' : '#3b82f6'
  const pctFill = `${rank}%`

  return (
    <div className="card p-4 space-y-3">
      <h3 className="panel-title flex items-center gap-2">
        <BarChart2 size={14} className="text-blue-light" aria-hidden />
        IV Rank
      </h3>

      <div className="flex items-end gap-4">
        <div className="text-3xl font-bold font-mono" style={{ color }}>
          {rank.toFixed(0)}
        </div>
        <div className="text-xs text-text-muted pb-1">/ 100</div>
      </div>

      {/* Bar gauge */}
      <div className="w-full h-3 rounded-full bg-surface-card overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: pctFill, backgroundColor: color }}
        />
      </div>

      <div className="grid grid-cols-3 text-xs text-text-muted gap-1">
        <div>
          <div className="text-text-secondary font-mono">{fmt(ivRank.current_iv, 1)}%</div>
          <div>Current IV</div>
        </div>
        <div>
          <div className="text-text-secondary font-mono">{fmt(ivRank.iv_52w_low, 1)}%</div>
          <div>52w Low</div>
        </div>
        <div>
          <div className="text-text-secondary font-mono">{fmt(ivRank.iv_52w_high, 1)}%</div>
          <div>52w High</div>
        </div>
      </div>

      {ivRank.interpretation && (
        <p className="text-xs text-text-muted border-t border-border pt-2 leading-relaxed">
          {ivRank.interpretation}
        </p>
      )}
    </div>
  )
}

// ── Put/Call ratio card ───────────────────────────────────────────────────────

function PCRCard() {
  const { chain } = useOptions()
  if (!chain) return null

  const pcr = chain.put_call_ratio
  const sentiment = pcr == null
    ? 'neutral'
    : pcr > 1.2 ? 'bearish'
    : pcr < 0.7 ? 'bullish'
    : 'neutral'

  const sentimentColor = sentiment === 'bullish' ? 'text-emerald' : sentiment === 'bearish' ? 'text-rose-light' : 'text-text-secondary'

  return (
    <div className="card p-4 space-y-2">
      <h3 className="panel-title">Put/Call Ratio</h3>
      <div className={clsx('text-3xl font-bold font-mono', sentimentColor)}>
        {pcr != null ? pcr.toFixed(2) : 'N/A'}
      </div>
      <div className="grid grid-cols-2 text-xs text-text-muted gap-1">
        <div><span className="text-text-secondary font-mono">{fmtK(chain.total_call_oi)}</span> Call OI</div>
        <div><span className="text-text-secondary font-mono">{fmtK(chain.total_put_oi)}</span> Put OI</div>
      </div>
      <div className={clsx('text-xs font-medium capitalize', sentimentColor)}>
        {sentiment}
      </div>
    </div>
  )
}

// ── Max Pain card ─────────────────────────────────────────────────────────────

function MaxPainCard() {
  const { maxPain, maxPainLoading, maxPainError, chain } = useOptions()

  if (maxPainLoading) return (
    <div className="card p-4">
      <p className="text-text-muted text-sm animate-pulse">Calculating max pain...</p>
    </div>
  )
  if (maxPainError || !maxPain) return (
    <div className="card p-4">
      <p className="text-text-muted text-xs">{maxPainError ?? 'Max pain unavailable'}</p>
    </div>
  )

  const underlying = maxPain.underlying_price ?? chain?.underlying_price ?? null
  const diff = underlying != null ? (((maxPain.max_pain_price ?? underlying) - underlying) / underlying * 100) : null

  return (
    <div className="card p-4 space-y-2">
      <h3 className="panel-title">Max Pain</h3>
      <div className="text-3xl font-bold font-mono text-amber-400">
        ${(maxPain.max_pain_price ?? 0).toFixed(2)}
      </div>
      {underlying != null && (
        <div className="text-xs text-text-muted">
          Underlying: <span className="text-text-secondary font-mono">${underlying.toFixed(2)}</span>
          {diff != null && (
            <span className={clsx('ml-2 font-medium', diff >= 0 ? 'text-emerald' : 'text-rose-light')}>
              {diff >= 0 ? '+' : ''}{diff.toFixed(1)}%
            </span>
          )}
        </div>
      )}
      <p className="text-xs text-text-muted">Strike where most options expire worthless</p>
    </div>
  )
}

// ── Options chain row ─────────────────────────────────────────────────────────

function ChainRow({
  call,
  put,
  strike,
  underlyingPrice,
  maxPainPrice,
}: {
  call?: OptionContract
  put?: OptionContract
  strike: number
  underlyingPrice: number | null
  maxPainPrice: number | null
}) {
  const isAtm = underlyingPrice != null && Math.abs(strike - underlyingPrice) / underlyingPrice < 0.01
  const isMaxPain = maxPainPrice != null && strike === maxPainPrice

  const callITM = call?.itm ?? false
  const putITM  = put?.itm ?? false

  return (
    <tr
      className={clsx(
        'border-b border-border/30 text-xs transition-colors',
        isAtm && 'ring-1 ring-inset ring-blue/30',
      )}
    >
      {/* CALL side */}
      <td className={clsx('py-1.5 pl-2 font-mono text-right pr-1', callITM && 'bg-blue/5')}>
        {call ? fmt(call.bid, 2, '$') : '—'}
      </td>
      <td className={clsx('py-1.5 font-mono text-right pr-1', callITM && 'bg-blue/5')}>
        {call ? fmt(call.ask, 2, '$') : '—'}
      </td>
      <td className={clsx('py-1.5 font-mono text-right pr-1', callITM && 'bg-blue/5')}>
        {call ? fmtK(call.volume) : '—'}
      </td>
      <td className={clsx('py-1.5 font-mono text-right pr-2', callITM && 'bg-blue/5')}>
        {call ? fmtK(call.open_interest) : '—'}
      </td>
      <td className={clsx('py-1.5 font-mono text-right pr-2 text-text-muted', callITM && 'bg-blue/5')}>
        {call?.implied_volatility != null ? `${(call.implied_volatility * 100).toFixed(1)}%` : '—'}
      </td>

      {/* STRIKE (centre column) */}
      <td className={clsx(
        'py-1.5 text-center font-mono font-semibold px-3',
        isMaxPain ? 'bg-amber-400/15 text-amber-400' : isAtm ? 'text-blue-light' : 'text-text-primary',
      )}>
        {isMaxPain && <span className="text-[9px] mr-0.5">MP</span>}
        ${strike.toFixed(0)}
      </td>

      {/* PUT side */}
      <td className={clsx('py-1.5 font-mono text-left pl-2', putITM && 'bg-rose/5')}>
        {put?.implied_volatility != null ? `${(put.implied_volatility * 100).toFixed(1)}%` : '—'}
      </td>
      <td className={clsx('py-1.5 font-mono text-left pl-1', putITM && 'bg-rose/5')}>
        {put ? fmtK(put.open_interest) : '—'}
      </td>
      <td className={clsx('py-1.5 font-mono text-left pl-1', putITM && 'bg-rose/5')}>
        {put ? fmtK(put.volume) : '—'}
      </td>
      <td className={clsx('py-1.5 font-mono text-left pl-1', putITM && 'bg-rose/5')}>
        {put ? fmt(put.bid, 2, '$') : '—'}
      </td>
      <td className={clsx('py-1.5 pr-2 font-mono text-left pl-1', putITM && 'bg-rose/5')}>
        {put ? fmt(put.ask, 2, '$') : '—'}
      </td>
    </tr>
  )
}

// ── Chain Table ───────────────────────────────────────────────────────────────

function ChainTable() {
  const { chain, chainLoading, chainError, maxPain } = useOptions()
  const [showAll, setShowAll] = useState(false)

  if (chainLoading) return (
    <div className="card p-6 text-center">
      <div className="h-40 skeleton mb-2" />
      <p className="text-text-muted text-sm">Loading options chain...</p>
    </div>
  )
  if (chainError) return (
    <div className="card p-6 text-center text-rose-light text-sm">
      {chainError}
    </div>
  )
  if (!chain) return null

  const underlying = chain.underlying_price
  const maxPainPrice = maxPain?.max_pain_price ?? null

  // Build strike → call/put map
  const callMap = new Map<number, OptionContract>()
  const putMap  = new Map<number, OptionContract>()
  chain.calls.forEach(c => callMap.set(c.strike, c))
  chain.puts.forEach(p => putMap.set(p.strike, p))

  const allStrikes = Array.from(
    new Set([...callMap.keys(), ...putMap.keys()])
  ).sort((a, b) => a - b)

  // Show ±15% of underlying by default to keep table manageable
  const displayStrikes = showAll
    ? allStrikes
    : underlying
      ? allStrikes.filter(s => Math.abs(s - underlying) / underlying <= 0.15)
      : allStrikes.slice(0, 40)

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 className="panel-title flex items-center gap-2">
          <Layers size={14} className="text-blue-light" aria-hidden />
          Options Chain — {chain.expiration}
          {underlying != null && (
            <span className="text-text-muted font-normal">
              · Underlying <span className="font-mono text-text-secondary">${(underlying ?? 0).toFixed(2)}</span>
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={() => setShowAll(v => !v)}
          className="text-xs text-blue-light hover:text-blue underline focus-visible:ring-1 focus-visible:ring-blue rounded"
        >
          {showAll ? 'Show ±15%' : `Show all ${allStrikes.length} strikes`}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs" role="grid">
          <thead>
            <tr className="border-b border-border">
              {/* Calls header */}
              <th colSpan={5} className="py-2 text-center text-emerald font-semibold tracking-wider bg-emerald/5">
                CALLS
              </th>
              <th className="py-2 text-center text-text-muted font-semibold bg-surface-card">STRIKE</th>
              {/* Puts header */}
              <th colSpan={5} className="py-2 text-center text-rose-light font-semibold tracking-wider bg-rose/5">
                PUTS
              </th>
            </tr>
            <tr className="border-b border-border text-text-muted">
              <th className="py-1.5 text-right pr-1 bg-emerald/5">Bid</th>
              <th className="py-1.5 text-right pr-1 bg-emerald/5">Ask</th>
              <th className="py-1.5 text-right pr-1 bg-emerald/5">Vol</th>
              <th className="py-1.5 text-right pr-2 bg-emerald/5">OI</th>
              <th className="py-1.5 text-right pr-2 bg-emerald/5">IV</th>
              <th className="py-1.5 bg-surface-card" />
              <th className="py-1.5 text-left pl-2 bg-rose/5">IV</th>
              <th className="py-1.5 text-left pl-1 bg-rose/5">OI</th>
              <th className="py-1.5 text-left pl-1 bg-rose/5">Vol</th>
              <th className="py-1.5 text-left pl-1 bg-rose/5">Bid</th>
              <th className="py-1.5 pr-2 text-left pl-1 bg-rose/5">Ask</th>
            </tr>
          </thead>
          <tbody>
            {displayStrikes.map(strike => (
              <ChainRow
                key={strike}
                strike={strike}
                call={callMap.get(strike)}
                put={putMap.get(strike)}
                underlyingPrice={underlying}
                maxPainPrice={maxPainPrice}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-2 border-t border-border text-xs text-text-muted flex gap-4">
        <span>
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-blue/20 mr-1" />
          ITM Call
        </span>
        <span>
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose/20 mr-1" />
          ITM Put
        </span>
        <span>
          <span className="inline-block px-1 rounded bg-amber-400/20 text-amber-400 mr-1 text-[9px]">MP</span>
          Max Pain strike
        </span>
        <span className="ml-auto">Showing {displayStrikes.length} of {allStrikes.length} strikes</span>
      </div>
    </div>
  )
}

// ── Unusual Activity Table ────────────────────────────────────────────────────

function UnusualActivity() {
  const { unusual, unusualLoading, unusualError } = useOptions()

  if (unusualLoading) return (
    <div className="card p-4">
      <p className="text-text-muted text-sm animate-pulse">Scanning for unusual activity...</p>
    </div>
  )

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <AlertTriangle size={14} className="text-amber-400" aria-hidden />
        <h3 className="panel-title">Unusual Activity</h3>
        {unusual && (
          <span className="ml-auto text-xs text-text-muted">{unusual.unusual.length} signals</span>
        )}
      </div>

      {unusualError && (
        <p className="p-4 text-xs text-text-muted">{unusualError}</p>
      )}

      {!unusual || unusual.unusual.length === 0 ? (
        <p className="p-4 text-xs text-text-muted">No unusual activity detected.</p>
      ) : (
        <div className="table-responsive overflow-x-auto">
          <table className="table-pro w-full text-xs">
            <thead>
              <tr>
                <th className="py-2 pl-4 pr-2">Type</th>
                <th className="!text-right py-2 pr-2">Strike</th>
                <th className="py-2 pr-2">Expiry</th>
                <th className="!text-right py-2 pr-2">Vol</th>
                <th className="!text-right py-2 pr-2">OI</th>
                <th className="!text-right py-2 pr-2">Vol/OI</th>
                <th className="!text-right py-2 pr-2">IV</th>
                <th className="!text-right py-2 pr-4">Est. Premium</th>
              </tr>
            </thead>
            <tbody>
              {unusual.unusual.map((t: UnusualOptionTrade, i) => (
                <tr key={`${t.contract_symbol}-${i}`}>
                  <td className="py-1.5 pl-4 pr-2">
                    <span className={t.option_type === 'call' ? 'badge-green text-[10px]' : 'badge-red text-[10px]'}>
                      {t.option_type.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-1.5 text-right pr-2 font-mono">${(t.strike ?? 0).toFixed(0)}</td>
                  <td className="py-1.5 pr-2 text-text-muted">{t.expiration}</td>
                  <td className="py-1.5 text-right pr-2 font-mono">{fmtK(t.volume)}</td>
                  <td className="py-1.5 text-right pr-2 font-mono text-text-muted">{fmtK(t.open_interest)}</td>
                  <td className={clsx(
                    'py-1.5 text-right pr-2 font-mono font-semibold',
                    t.volume_oi_ratio >= 5 ? 'text-amber-400' : 'text-text-secondary',
                  )}>
                    {(t.volume_oi_ratio ?? 0).toFixed(1)}x
                  </td>
                  <td className="py-1.5 text-right pr-2 font-mono text-text-muted">
                    {t.implied_volatility != null ? `${(t.implied_volatility * 100).toFixed(1)}%` : '—'}
                  </td>
                  <td className="py-1.5 text-right pr-4 font-mono text-text-secondary">
                    {fmtPremium(t.estimated_premium)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function OptionsPage() {
  const {
    ticker, expiry, expirations,
    chain, chainLoading,
    setExpiry,
    loadAll, fetchChain, fetchMaxPain,
  } = useOptions()

  const [inputVal, setInputVal] = useState(ticker || 'AAPL')

  const handleLoad = useCallback(() => {
    const t = inputVal.trim().toUpperCase()
    if (t) loadAll(t)
  }, [inputVal, loadAll])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLoad()
  }

  const handleExpiryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newExpiry = e.target.value
    setExpiry(newExpiry)
    if (ticker) {
      fetchChain(ticker, newExpiry)
      fetchMaxPain(ticker, newExpiry)
    }
  }

  // Load default on mount
  useEffect(() => {
    if (!chain && !chainLoading) {
      loadAll(inputVal.trim().toUpperCase())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <PageHeader
        title="Options Analysis"
        icon={Layers}
        subtitle="Chain · Greeks · IV Rank · Max Pain · Unusual Activity"
        actions={
          <>
            <div className="relative w-28">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" aria-hidden />
              <input
                type="text"
                value={inputVal}
                onChange={e => setInputVal(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                placeholder="AAPL"
                className="input pl-8 pr-3 py-2 font-mono"
                aria-label="Ticker symbol"
              />
            </div>

            {expirations.length > 0 && (
              <select
                value={expiry}
                onChange={handleExpiryChange}
                className="px-3 py-2 rounded-lg border border-border bg-surface-card text-text-secondary
                           text-sm focus:outline-none focus:ring-2 focus:ring-blue/50"
                aria-label="Select expiry date"
              >
                {expirations.map(e => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
            )}

            <button
              type="button"
              onClick={handleLoad}
              className="btn-primary text-sm"
            >
              Load
            </button>
          </>
        }
      />

      {/* Summary row */}
      {chain && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center stagger-children">
          <div className="card p-3">
            <div className="panel-title mb-1">Underlying</div>
            <div className="stat-hero text-text-primary">
              {chain.underlying_price != null ? `$${chain.underlying_price.toFixed(2)}` : 'N/A'}
            </div>
          </div>
          <div className="card p-3">
            <div className="panel-title mb-1">Expiry</div>
            <div className="font-mono font-semibold text-text-secondary text-sm pt-1">{chain.expiration}</div>
          </div>
          <div className="card p-3">
            <div className="panel-title mb-1">Total Calls</div>
            <div className="font-mono font-bold text-emerald text-lg flex items-center justify-center gap-1">
              <TrendingUp size={14} aria-hidden />
              {chain.calls.length}
            </div>
          </div>
          <div className="card p-3">
            <div className="panel-title mb-1">Total Puts</div>
            <div className="font-mono font-bold text-rose-light text-lg flex items-center justify-center gap-1">
              <TrendingDown size={14} aria-hidden />
              {chain.puts.length}
            </div>
          </div>
        </div>
      )}

      {/* Metrics row: IV rank, PCR, max pain */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <IVRankGauge />
        <PCRCard />
        <MaxPainCard />
      </div>

      {/* Options chain table */}
      <ChainTable />

      {/* Unusual activity */}
      <UnusualActivity />
    </div>
  )
}
